import { useState } from "react";
import { Link } from "react-router-dom";
import { RocketLaunch, ChatCircleText, CalendarCheck, Wrench, ArrowRight } from "@phosphor-icons/react";
import { act, api, fmtDate } from "@/lib/api";
import { Badge } from "@/components/common";
import { FixGuide } from "@/components/FixGuide";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Card = ({ icon: Icon, tone, title, sub, children, testid }) => (
  <div className="border border-border rounded-md p-4 bg-black/30 animate-in fade-in slide-in-from-bottom-1 duration-300" data-testid={testid}>
    <div className="flex items-start gap-3">
      <Icon size={20} weight="bold" className={tone} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white">{title}</div>
        {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
        {children}
      </div>
    </div>
  </div>
);

const resolve = (id, decision, text = "", msg = "Done") => act(() => api.post(`/exceptions/${id}/resolve`, { decision, text }), msg);

export const CampaignCard = ({ c, onDone }) => {
  const [open, setOpen] = useState(false);
  const ready = c.status === "ready";
  const go = async () => { if (await act(() => api.post(`/launches/${c.id}/launch`), "Approved - MaSa is sending everything now")) onDone(); };
  return (
    <Card icon={RocketLaunch} tone="text-[#C084FC]" testid={`one-campaign-${c.id}`} title={c.name}
      sub={`${c.contacts} contacts · ${ready ? "ready for your approval" : c.status === "failed" ? "build failed" : `building ${c.steps_done}/${c.steps_total}`}`}>
      <div className="flex flex-wrap gap-2 mt-3">
        {ready && <Button size="sm" data-testid={`one-approve-campaign-${c.id}`} onClick={() => setOpen(true)} className="bg-gradient-to-r from-[#6D28D9] via-[#C026D3] to-[#F97316] text-white hover:opacity-90">Approve & launch everything</Button>}
        <Button asChild size="sm" variant="secondary" data-testid={`one-review-campaign-${c.id}`}><Link to={`/launchpad/${c.id}`}>Review first <ArrowRight /></Link></Button>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="one-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Launch "{c.name}"?</AlertDialogTitle>
            <AlertDialogDescription>One approval covers everything: the 3-email sequence to {c.contacts} contacts (at the warm-up pace), the newsletter, social posts, carousel and video.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="one-approve-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="one-approve-confirm" onClick={go}>Approve & launch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export const ReplyCard = ({ e, onDone }) => {
  const [text, setText] = useState(e.draft || "");
  const who = e.contact ? `${e.contact.name}${e.contact.company ? ` · ${e.contact.company}` : ""}` : e.owner;
  const hot = /interested|hot|meeting/i.test(`${e.type} ${e.title}`);
  return (
    <Card icon={ChatCircleText} tone={hot ? "text-[#F97316]" : "text-sky-400"} testid={`one-reply-${e.id}`} title={e.title} sub={`${who || ""} · ${fmtDate(e.created_at)}`}>
      <p className="text-xs text-zinc-400 mt-2">{e.reason}</p>
      {e.type === "reply_draft" && <Textarea data-testid={`one-reply-text-${e.id}`} rows={4} value={text} onChange={(x) => setText(x.target.value)} className="bg-black mt-2 text-sm" />}
      <div className="flex flex-wrap gap-2 mt-3">
        {e.type === "reply_draft"
          ? <Button size="sm" data-testid={`one-reply-send-${e.id}`} onClick={async () => { if (await resolve(e.id, "approve", text !== e.draft ? text : "", "Reply sent")) onDone(); }}>Approve & send</Button>
          : <Button size="sm" data-testid={`one-reply-done-${e.id}`} onClick={async () => { if (await resolve(e.id, "approve", "", "Marked as handled")) onDone(); }}>Mark handled</Button>}
        {e.thread_id && <Button asChild size="sm" variant="secondary" data-testid={`one-reply-open-${e.id}`}><Link to={`/inbox?thread=${e.thread_id}`}>Open conversation</Link></Button>}
        <Button size="sm" variant="ghost" data-testid={`one-reply-dismiss-${e.id}`} onClick={async () => { if (await resolve(e.id, "reject", "", "Dismissed")) onDone(); }}>Dismiss</Button>
      </div>
    </Card>
  );
};

export const FixCard = ({ e, onDone }) => (
  <Card icon={Wrench} tone="text-red-400" testid={`one-fix-${e.id}`} title={e.title} sub={`${e.owner} · ${fmtDate(e.created_at)}`}>
    <p className="text-xs text-zinc-400 mt-2">{e.reason}</p>
    <FixGuide id={e.id} guide={e.guide} />
    <Button size="sm" variant="secondary" className="mt-3" data-testid={`one-fix-done-${e.id}`} onClick={async () => { if (await resolve(e.id, "approve", "", "Marked as fixed - MaSa will confirm on its next check")) onDone(); }}>I've done this</Button>
  </Card>
);

export const MeetingCard = ({ x }) => (
  <Card icon={CalendarCheck} tone="text-emerald-400" testid={`one-meeting-${x.id}`}
    title={new Date(x.start).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
    sub={`${x.contact?.name || "Contact"}${x.contact?.company ? ` · ${x.contact.company}` : ""}${x.rep_name ? ` · with ${x.rep_name}` : ""}`}>
    {x.link && <a href={x.link} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline mt-2 inline-block" data-testid={`one-meeting-link-${x.id}`}>Meeting link</a>}
  </Card>
);

export const Section = ({ title, items, empty, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">{title}</span><Badge v={String(items.length)} tone={items.length ? "blue" : "gray"} /></div>
    <div className="space-y-2">{items.length ? children : <p className="text-xs text-zinc-600">{empty}</p>}</div>
  </div>
);
